/**
 * Editor de contenido del Clapper Arcade (Learning/People — spec §7).
 *
 * Correcciones Learning 07/09/2026 (Google Doc §16):
 *  §1 Textos de la interfaz del juego (portada → cierre) e imágenes editables.
 *  §2 Las dinámicas (ítems de los System Checks / switches de los minijuegos)
 *     se editan de forma visual: agregar, quitar, reordenar, tipo, textos ×3.
 *  §3 "Duplicar paso" genera un id único (copiar a mano el JSON repetía el id
 *     y el schema lo rechazaba); "Nuevo paso" ofrece plantillas por tipo.
 *  §4 Video y link externo por idioma (ES/EN/PT) con fallback al general.
 * El JSON crudo sigue disponible como modo avanzado.
 */
import { currentLocale } from '@/lib/locale';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowDown, ArrowUp, Copy, History, Pencil, Plus, Save, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { trpc } from '@/lib/trpc';
import { useTranslation } from 'react-i18next';
import {
  ARCADE_LANGS,
  MINIGAME_KINDS,
  arcadeContentSchema,
  type ArcadeCheckItem,
  type ArcadeContent,
  type ArcadeLang,
  type ArcadeStep,
  type I18nText,
  type I18nUrl,
  type MinigameKind,
} from '@/../../shared/people-learning';
import { LearningImageField } from './LearningImageField';

type Step = ArcadeStep & { [k: string]: unknown };
type UiStrings = Record<ArcadeLang, Record<string, string>>;

const newId = (prefix = 's') => `${prefix}_${Date.now().toString(36)}${Math.floor(Math.random() * 1000)}`;

/** Un id que no exista en todo el contenido (§3): base_copy, base_copy2, … */
function uniqueId(content: ArcadeContent, base: string): string {
  const all = new Set(content.levels.flatMap((l) => l.steps.map((s) => s.id)));
  if (!all.has(base)) return base;
  let n = 1;
  while (all.has(`${base}${n === 1 ? '' : n}`)) n++;
  return `${base}${n === 1 ? '' : n}`;
}

/** Plantillas de minijuego (§3): data mínima válida para cada tipo, lista para editar. */
const MINIGAME_TEMPLATES: Record<MinigameKind, { title: I18nText; data: Record<string, unknown>; items?: ArcadeCheckItem[] }> = {
  dial: {
    title: { es: 'Nuevo diagnóstico (diales)', en: 'New diagnostic (dials)', pt: 'Novo diagnóstico (dials)' },
    data: { dims: ['Effective', 'Affective', 'Perspective'], dials: [{ dim: 0, target: 2 }, { dim: 1, target: 2 }, { dim: 2, target: 0 }] },
    items: [{ id: 't1', kind: 'toggle', label: { es: 'Completé la tarea', en: 'I completed the task', pt: 'Completei a tarefa' } }],
  },
  cables: {
    title: { es: 'Nueva unión de cables', en: 'New cable matching', pt: 'Nova ligação de cabos' },
    data: {
      pillars: ['Pilar A', 'Pilar B', 'Pilar C'],
      pairs: [0, 1, 2],
      decisions: [{ es: 'Decisión 1' }, { es: 'Decisión 2' }, { es: 'Decisión 3' }],
    },
  },
  virtues: {
    title: { es: 'Nueva selección de virtudes', en: 'New virtues pick', pt: 'Nova escolha de virtudes' },
    data: { virtues: ['People First', 'Build Trust', 'Hands-on', 'Bar Raiser', 'Transparency', 'Whole Company'], pick: 3 },
  },
  dragdrop: {
    title: { es: 'Nuevo arrastrar y soltar', en: 'New drag & drop', pt: 'Novo arrastar e soltar' },
    data: {
      zones: { es: ['Zona 1', 'Zona 2'], en: ['Zone 1', 'Zone 2'], pt: ['Zona 1', 'Zona 2'] },
      fragments: [{ es: 'Fragmento A' }, { es: 'Fragmento B' }],
      mapping: [0, 1],
      distractor: false,
    },
  },
  hotspot: {
    title: { es: 'Nueva auditoría (hotspots)', en: 'New audit (hotspots)', pt: 'Nova auditoria (hotspots)' },
    data: {
      hotspots: [
        { label: { es: 'Práctica incorrecta' }, x: '20%', y: '30%', good: true },
        { label: { es: 'Práctica correcta (distractor)' }, x: '70%', y: '60%', good: false },
      ],
    },
  },
  impact_role: {
    title: { es: 'Nueva autocalibración de impacto', en: 'New impact self-calibration', pt: 'Nova autocalibração de impacto' },
    data: {},
  },
};

export function ContentEditorTab() {
  const { t } = useTranslation();
  const arcade = trpc.peopleLearning.getMyArcade.useQuery(undefined, { staleTime: 30_000 });
  const utils = trpc.useUtils();
  const save = trpc.peopleLearning.saveContent.useMutation({
    onSuccess: (r) => {
      utils.peopleLearning.getMyArcade.invalidate();
      utils.peopleLearning.getContentHistory.invalidate();
      toast.success(t('people-learning.contenteditortab.publicado', 'Publicado — versión {{v}}', { v: r.version }));
    },
    onError: (e) => toast.error(e.message),
  });
  const history = trpc.peopleLearning.getContentHistory.useQuery(undefined, { staleTime: 30_000 });
  const revert = trpc.peopleLearning.revertContent.useMutation({
    onSuccess: () => {
      utils.peopleLearning.getMyArcade.invalidate();
      utils.peopleLearning.getContentHistory.invalidate();
      setDraft(null);
      toast.success(t('people-learning.contenteditortab.restaurado', 'Versión restaurada'));
    },
    onError: (e) => toast.error(e.message),
  });

  const [draft, setDraft] = useState<ArcadeContent | null>(null);
  const [editing, setEditing] = useState<{ levelIdx: number; stepIdx: number } | null>(null);
  const [rawMode, setRawMode] = useState(false);
  const [rawText, setRawText] = useState('');
  const [rawError, setRawError] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (arcade.data?.content && !draft) {
      setDraft(JSON.parse(JSON.stringify(arcade.data.content)));
    }
  }, [arcade.data?.content, draft]);

  // Deep-link desde el lapicito del juego: ?step=<id> abre ese paso
  useEffect(() => {
    const stepId = searchParams.get('step');
    if (!stepId || !draft || editing) return;
    for (let li = 0; li < draft.levels.length; li++) {
      const si = draft.levels[li].steps.findIndex((s) => s.id === stepId);
      if (si >= 0) { setEditing({ levelIdx: li, stepIdx: si }); break; }
    }
    const params = new URLSearchParams(searchParams);
    params.delete('step');
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, searchParams]);

  const publish = (content: ArcadeContent) => {
    // Chequeo previo con mensaje claro (§3): el server lo rechazaría igual, pero "stepId duplicado: x" solo no ayuda.
    const seen = new Map<string, string>();
    for (const l of content.levels)
      for (const s of l.steps) {
        if (seen.has(s.id)) {
          toast.error(
            t('people-learning.contenteditortab.id_duplicado', 'Hay dos pasos con el mismo id "{{id}}" ("{{a}}" y "{{b}}"). Usá "Duplicar" para copiar un paso: genera un id nuevo solo.', {
              id: s.id, a: seen.get(s.id), b: s.title.es,
            })
          );
          return;
        }
        seen.set(s.id, s.title.es);
      }
    save.mutate({ content });
  };

  const patchDraft = (fn: (d: ArcadeContent) => void) =>
    setDraft((d) => {
      if (!d) return d;
      const next = JSON.parse(JSON.stringify(d)) as ArcadeContent;
      fn(next);
      return next;
    });

  const moveStep = (li: number, si: number, dir: -1 | 1) =>
    patchDraft((d) => {
      const steps = d.levels[li].steps;
      const j = si + dir;
      if (j < 0 || j >= steps.length) return;
      [steps[si], steps[j]] = [steps[j], steps[si]];
    });

  const removeStep = (li: number, si: number) =>
    patchDraft((d) => { d.levels[li].steps.splice(si, 1); });

  /** §3: copia profunda con id nuevo (y título "(copia)"), insertada debajo del original. */
  const duplicateStep = (li: number, si: number) =>
    patchDraft((d) => {
      const src = d.levels[li].steps[si];
      const copy = JSON.parse(JSON.stringify(src)) as ArcadeStep;
      copy.id = uniqueId(d, `${src.id}_copy`);
      copy.title = { ...copy.title, es: `${copy.title.es} (copia)` };
      d.levels[li].steps.splice(si + 1, 0, copy);
    });

  const addStep = (li: number, type: 'video' | 'check' | MinigameKind) =>
    patchDraft((d) => {
      let step: ArcadeStep;
      if (type === 'video') step = { id: uniqueId(d, newId('v')), type: 'video', title: { es: 'Nuevo video' } };
      else if (type === 'check')
        step = {
          id: uniqueId(d, newId('sc')),
          type: 'check',
          title: { es: 'Nuevo System Check' },
          items: [{ id: 't1', kind: 'toggle', label: { es: 'Completé la tarea', en: 'I completed the task', pt: 'Completei a tarefa' } }],
        };
      else {
        const tpl = MINIGAME_TEMPLATES[type];
        step = { id: uniqueId(d, newId('sc')), type: 'minigame', kind: type, title: tpl.title, data: JSON.parse(JSON.stringify(tpl.data)), items: tpl.items } as ArcadeStep;
      }
      d.levels[li].steps.push(step);
      toast.info(t('people-learning.contenteditortab.paso_agregado', 'Paso agregado al final del nivel. Editalo con el lápiz y después publicá.'));
    });

  if (arcade.isLoading || !draft) return <WinclapLoader />;

  return (
    <div className="max-w-4xl space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm text-muted-foreground">
          {t('people-learning.contenteditortab.los_cambios_no_afectan_el_progreso')}
        </p>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowHistory(true)}>
            <History className="mr-1.5 h-3.5 w-3.5" /> Historial
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (!rawMode) { setRawText(JSON.stringify(draft, null, 2)); setRawError(''); }
              setRawMode(!rawMode);
            }}
          >
            {rawMode ? t('people-learning.contenteditortab.modo_visual') : t('people-learning.contenteditortab.editar_json')}
          </Button>
          {!rawMode && (
            <Button size="sm" disabled={save.isPending} onClick={() => publish(draft)}>
              <Save className="mr-1.5 h-3.5 w-3.5" /> Publicar
            </Button>
          )}
        </div>
      </div>

      {rawMode ? (
        <Card className="space-y-3 p-4">
          <Textarea
            value={rawText}
            onChange={(e) => { setRawText(e.target.value); setRawError(''); }}
            rows={24}
            className="font-mono text-xs"
          />
          {rawError && <p className="text-xs text-destructive">{rawError}</p>}
          <Button
            size="sm"
            disabled={save.isPending}
            onClick={() => {
              try {
                const parsed = arcadeContentSchema.safeParse(JSON.parse(rawText));
                if (!parsed.success) {
                  const i = parsed.error.issues[0];
                  setRawError(`${i.path.join('.')}: ${i.message}`);
                  return;
                }
                setDraft(parsed.data);
                publish(parsed.data);
                setRawMode(false);
              } catch (e: any) {
                setRawError(`JSON inválido: ${e.message}`);
              }
            }}
          >
            <Save className="mr-1.5 h-3.5 w-3.5" /> {t('people-learning.contenteditortab.validar_y_publicar')}
          </Button>
        </Card>
      ) : (
        <>
          {/* General */}
          <Card className="space-y-3 p-4">
            <h3 className="font-semibold">General</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>{t('people-learning.contenteditortab.fecha_de_lanzamiento_corte_new_clapper')}</Label>
                <Input
                  type="date"
                  value={draft.launchDate}
                  onChange={(e) => patchDraft((d) => { d.launchDate = e.target.value; })}
                  className="h-8 w-fit"
                />
              </div>
              <div className="space-y-1.5">
                <Label>{t('people-learning.contenteditortab.carpeta_de_drive_con_los_videos')}</Label>
                <Input
                  value={draft.driveFolder ?? ''}
                  onChange={(e) => patchDraft((d) => { d.driveFolder = e.target.value || undefined; })}
                  className="h-8"
                  placeholder="https://drive.google.com/drive/folders/…"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>{t('people-learning.contenteditortab.areas_separadas_por_coma_opciones_del')}</Label>
              <Input
                value={draft.areas.join(', ')}
                onChange={(e) =>
                  patchDraft((d) => { d.areas = e.target.value.split(',').map((s) => s.trim()).filter(Boolean); })
                }
                className="h-8"
              />
            </div>
          </Card>

          {/* §1 Imágenes del juego */}
          <Card className="space-y-3 p-4">
            <h3 className="font-semibold">{t('people-learning.contenteditortab.imagenes_del_juego', 'Imágenes del juego')}</h3>
            <p className="text-xs text-muted-foreground">
              {t('people-learning.contenteditortab.imagenes_hint', 'Reemplazan al emoji y al logo por defecto. Se muestran en la portada, el HUD y el cuadro de Facu.')}
            </p>
            <div className="grid gap-4 sm:grid-cols-3">
              <LearningImageField
                folder="arcade" aspect="square"
                imageKey={draft.assets?.facuAvatarKey}
                onChange={(k) => patchDraft((d) => { d.assets = { ...(d.assets ?? {}), facuAvatarKey: k }; })}
                label={t('people-learning.contenteditortab.avatar_de_facu', 'Avatar de Facu (guía)')}
              />
              <LearningImageField
                folder="arcade"
                imageKey={draft.assets?.coverImageKey}
                onChange={(k) => patchDraft((d) => { d.assets = { ...(d.assets ?? {}), coverImageKey: k }; })}
                label={t('people-learning.contenteditortab.imagen_de_portada', 'Imagen de portada')}
              />
              <LearningImageField
                folder="arcade"
                imageKey={draft.assets?.logoKey}
                onChange={(k) => patchDraft((d) => { d.assets = { ...(d.assets ?? {}), logoKey: k }; })}
                label={t('people-learning.contenteditortab.logo', 'Logo (HUD)')}
              />
            </div>
          </Card>

          {/* §1 Textos de la interfaz */}
          <UiStringsCard
            value={draft.uiStrings}
            onChange={(next) => patchDraft((d) => { d.uiStrings = next; })}
          />

          {/* Nivel 5: videos por área */}
          <Card className="space-y-3 p-4">
            <h3 className="font-semibold">{t('people-learning.contenteditortab.nivel_5_video_del_lider_por')}</h3>
            <p className="text-xs text-muted-foreground">
              {t('people-learning.contenteditortab.pega_el_link_de_drive_del')}
            </p>
            {draft.areas.map((area) => (
              <div key={area} className="grid grid-cols-1 items-center gap-2 sm:grid-cols-[200px_1fr]">
                <span className="text-sm font-medium">{area}</span>
                <Input
                  value={draft.areaVideos[area] ?? ''}
                  onChange={(e) =>
                    patchDraft((d) => {
                      if (e.target.value) d.areaVideos[area] = e.target.value;
                      else delete d.areaVideos[area];
                    })
                  }
                  className="h-8 text-sm"
                  placeholder="https://drive.google.com/file/d/…"
                />
              </div>
            ))}
          </Card>

          {/* Niveles y pasos */}
          {draft.levels.map((lvl, li) => (
            <Card key={lvl.id} className="space-y-2 p-4">
              <div className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full" style={{ backgroundColor: lvl.accent }} />
                <h3 className="font-semibold">Nivel {li + 1}: {lvl.title.es}</h3>
                <div className="ml-auto">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="h-7 text-xs">
                        <Plus className="mr-1 h-3 w-3" /> {t('people-learning.contenteditortab.nuevo_paso', 'Nuevo paso')}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => addStep(li, 'video')}>🎬 {t('people-learning.contenteditortab.tipo_video', 'Video')}</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => addStep(li, 'check')}>✅ {t('people-learning.contenteditortab.tipo_check', 'System Check (checklist)')}</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel className="text-xs">{t('people-learning.contenteditortab.minijuegos', 'Minijuegos (plantilla editable)')}</DropdownMenuLabel>
                      {MINIGAME_KINDS.map((k) => (
                        <DropdownMenuItem key={k} onClick={() => addStep(li, k)}>🕹️ {MINIGAME_TEMPLATES[k].title.es}</DropdownMenuItem>
                      ))}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => window.open('/clapper-arcade/banco-actividades.html', '_blank', 'noopener')}>
                        📚 {t('people-learning.contenteditortab.banco_de_actividades', 'Banco de actividades (catálogo)')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              <UrlByLangFields
                label={t('people-learning.contenteditortab.video_de_facu', 'Video de Facu al entrar al nivel (opcional)')}
                general={lvl.facuVideoUrl ?? ''}
                byLang={lvl.facuVideoUrlByLang}
                onGeneral={(v) => patchDraft((d) => { d.levels[li].facuVideoUrl = v || undefined; })}
                onByLang={(v) => patchDraft((d) => { d.levels[li].facuVideoUrlByLang = v; })}
                placeholder="https://drive.google.com/file/d/… o .mp4"
              />
              <div className="divide-y">
                {lvl.steps.map((step, si) => (
                  <div key={step.id} className="flex items-center gap-2 py-2">
                    <Badge variant="secondary" className="w-20 justify-center text-[10px]">
                      {step.type === 'minigame' ? (step as any).kind : step.type}
                    </Badge>
                    <span className="min-w-0 flex-1 truncate text-sm">
                      {step.title.es}
                      {step.optional && <span className="ml-1.5 text-xs text-amber-600">bonus</span>}
                      {step.onlyCountry && <span className="ml-1.5 text-xs text-muted-foreground">solo {step.onlyCountry}</span>}
                      {step.type === 'video' && (step as any).byArea && (
                        <span className="ml-1.5 text-xs text-muted-foreground">{t('people-learning.contenteditortab.por_area')}</span>
                      )}
                    </span>
                    {step.type === 'video' && (
                      <span className={`text-xs ${(step as any).videoUrl || (step as any).byArea ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                        {(step as any).byArea ? '—' : (step as any).videoUrl ? t('people-learning.contenteditortab.con_video') : t('people-learning.contenteditortab.sin_video')}
                      </span>
                    )}
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => moveStep(li, si, -1)} title={t('people-learning.contenteditortab.subir', 'Subir')}>
                      <ArrowUp className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => moveStep(li, si, 1)} title={t('people-learning.contenteditortab.bajar', 'Bajar')}>
                      <ArrowDown className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditing({ levelIdx: li, stepIdx: si })} title={t('people-learning.contenteditortab.editar_paso')}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => duplicateStep(li, si)} title={t('people-learning.contenteditortab.duplicar', 'Duplicar (id nuevo)')}>
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                      onClick={() => { if (window.confirm(`¿Quitar "${step.title.es}"?`)) removeStep(li, si); }}
                      title={t('people-learning.contenteditortab.quitar', 'Quitar')}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </>
      )}

      {editing && draft && draft.levels[editing.levelIdx]?.steps[editing.stepIdx] && (
        <StepEditorDialog
          step={draft.levels[editing.levelIdx].steps[editing.stepIdx] as Step}
          onClose={() => setEditing(null)}
          onSave={(next) => {
            patchDraft((d) => { d.levels[editing.levelIdx].steps[editing.stepIdx] = next; });
            setEditing(null);
          }}
        />
      )}

      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>{t('people-learning.contenteditortab.historial_de_versiones')}</DialogTitle></DialogHeader>
          {(history.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('people-learning.contenteditortab.todavia_no_hay_versiones_publicadas_rige')}</p>
          ) : (
            <div className="space-y-2">
              {(history.data ?? []).map((v) => (
                <div key={v.version} className="flex items-center gap-3 rounded-md border p-2.5 text-sm">
                  <span className="font-semibold">v{v.version}</span>
                  <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                    {new Date(v.updatedAt).toLocaleString(currentLocale(), { dateStyle: 'short', timeStyle: 'short' })} · {v.updatedBy}
                    {v.note ? ` · ${v.note}` : ''}
                  </span>
                  <Button
                    size="sm" variant="outline" className="h-7 text-xs"
                    disabled={revert.isPending}
                    onClick={() => { if (window.confirm(`¿Restaurar la v${v.version} como versión nueva?`)) revert.mutate({ version: v.version }); }}
                  >
                    Restaurar
                  </Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** §1 Textos de la interfaz del juego: defaults de /clapper-arcade/ui-strings.json + overrides guardados en el contenido. */
function UiStringsCard({ value, onChange }: { value: ArcadeContent['uiStrings']; onChange: (v: ArcadeContent['uiStrings']) => void }) {
  const { t } = useTranslation();
  const [defaults, setDefaults] = useState<UiStrings | null>(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  useEffect(() => {
    fetch('/clapper-arcade/ui-strings.json').then((r) => r.json()).then(setDefaults).catch(() => setDefaults(null));
  }, []);
  const keys = useMemo(() => (defaults ? Object.keys(defaults.es) : []), [defaults]);
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return keys;
    return keys.filter((k) => k.toLowerCase().includes(s) || ARCADE_LANGS.some((l) => ((value?.[l]?.[k] ?? defaults?.[l]?.[k]) || '').toLowerCase().includes(s)));
  }, [keys, q, value, defaults]);
  const overrides = useMemo(() => ARCADE_LANGS.reduce((a, l) => a + Object.keys(value?.[l] ?? {}).length, 0), [value]);

  const set = (lang: ArcadeLang, key: string, text: string) => {
    const next: NonNullable<ArcadeContent['uiStrings']> = { ...(value ?? {}) };
    const byLang = { ...(next[lang] ?? {}) };
    if (!text || text === defaults?.[lang]?.[key]) delete byLang[key];
    else byLang[key] = text;
    if (Object.keys(byLang).length) next[lang] = byLang;
    else delete next[lang];
    onChange(Object.keys(next).length ? next : undefined);
  };

  return (
    <Card className="space-y-3 p-4">
      <div className="flex items-center gap-2">
        <h3 className="font-semibold">{t('people-learning.contenteditortab.textos_de_la_interfaz', 'Textos de la interfaz del juego')}</h3>
        {overrides > 0 && <Badge variant="secondary">{overrides} {t('people-learning.contenteditortab.personalizados', 'personalizados')}</Badge>}
        <Button variant="outline" size="sm" className="ml-auto h-7 text-xs" onClick={() => setOpen((o) => !o)}>
          {open ? t('people-learning.contenteditortab.ocultar', 'Ocultar') : t('people-learning.contenteditortab.editar_textos', 'Editar textos')}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        {t('people-learning.contenteditortab.textos_hint', 'Portada, HUD, mapa de niveles, botones, mensajes de los checks y los diálogos de Facu, en los 3 idiomas. Dejá un campo igual al default para no personalizarlo.')}
      </p>
      {open && (
        defaults ? (
          <div className="space-y-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} className="h-8 pl-8 text-sm" placeholder={t('people-learning.contenteditortab.buscar_texto', 'Buscar texto o clave…')} />
            </div>
            <div className="max-h-[480px] space-y-3 overflow-y-auto pr-1">
              {shown.map((k) => (
                <div key={k} className="rounded-md border p-2.5">
                  <p className="mb-1.5 font-mono text-[11px] text-muted-foreground">{k}</p>
                  <div className="grid gap-2 sm:grid-cols-3">
                    {ARCADE_LANGS.map((l) => {
                      const current = value?.[l]?.[k] ?? defaults[l]?.[k] ?? '';
                      const custom = !!value?.[l]?.[k];
                      return (
                        <Textarea
                          key={l}
                          rows={2}
                          value={current}
                          onChange={(e) => set(l, k, e.target.value)}
                          className={`text-xs ${custom ? 'border-brand-green' : ''}`}
                          placeholder={l.toUpperCase()}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-xs text-destructive">{t('people-learning.contenteditortab.sin_defaults', 'No pude cargar los textos por defecto del juego.')}</p>
        )
      )}
    </Card>
  );
}

function I18nFields({ label, value, onChange, rows }: {
  label: string;
  value: I18nText;
  onChange: (v: I18nText) => void;
  rows?: number;
}) {
  const { t } = useTranslation();
  const Comp = rows ? Textarea : Input;
  return (
    <div className="space-y-1.5">
      <Label>{label} {t('people-learning.contenteditortab.es_en_pt_vacio_cae_a')}</Label>
      <div className="grid gap-2 sm:grid-cols-3">
        {ARCADE_LANGS.map((l) => (
          <Comp
            key={l}
            value={value[l] ?? ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onChange({ ...value, [l]: e.target.value || (l === 'es' ? '' : undefined) })}
            className="text-sm"
            placeholder={l.toUpperCase()}
            rows={rows}
          />
        ))}
      </div>
    </div>
  );
}

/** §4 URL general + por idioma. */
function UrlByLangFields({ label, general, byLang, onGeneral, onByLang, placeholder, disabled }: {
  label: string;
  general: string;
  byLang: I18nUrl | undefined;
  onGeneral: (v: string) => void;
  onByLang: (v: I18nUrl | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [perLang, setPerLang] = useState(!!byLang && Object.values(byLang).some(Boolean));
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <Switch checked={perLang} disabled={disabled} onCheckedChange={(v) => { setPerLang(v); if (!v) onByLang(undefined); }} />
          {t('people-learning.contenteditortab.por_idioma', 'Distinto por idioma')}
        </label>
      </div>
      <Input value={general} onChange={(e) => onGeneral(e.target.value)} className="h-8 text-sm" placeholder={placeholder} disabled={disabled} />
      {perLang && !disabled && (
        <div className="grid gap-2 sm:grid-cols-3">
          {ARCADE_LANGS.map((l) => (
            <Input
              key={l}
              value={byLang?.[l] ?? ''}
              onChange={(e) => onByLang({ ...(byLang ?? {}), [l]: e.target.value || undefined })}
              className="h-8 text-xs"
              placeholder={`${l.toUpperCase()} · ${t('people-learning.contenteditortab.vacio_usa_general', 'vacío = usa el general')}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** §2 Editor visual de los ítems de un System Check (o del switch que acompaña a un minijuego). */
function CheckItemsEditor({ items, onChange, max }: { items: ArcadeCheckItem[]; onChange: (v: ArcadeCheckItem[]) => void; max: number }) {
  const { t } = useTranslation();
  const update = (i: number, patch: Partial<ArcadeCheckItem>) => onChange(items.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const KIND_LABEL: Record<ArcadeCheckItem['kind'], string> = {
    toggle: t('people-learning.contenteditortab.kind_toggle', 'Switch (debe activarse)'),
    neutral: t('people-learning.contenteditortab.kind_neutral', 'Switch opcional (no se valida)'),
    multi: t('people-learning.contenteditortab.kind_multi', 'Multi (marcar / intruso)'),
    select: t('people-learning.contenteditortab.kind_select', 'Selector (una correcta)'),
  };
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={it.id} className="space-y-2 rounded-md border p-2.5">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[11px] text-muted-foreground">{it.id}</span>
            <select
              value={it.kind}
              onChange={(e) => update(i, { kind: e.target.value as ArcadeCheckItem['kind'], options: e.target.value === 'select' ? (it.options ?? [{ id: 'o1', label: { es: 'Opción 1' }, correct: true }]) : undefined })}
              className="h-7 rounded-md border bg-background px-2 text-xs"
            >
              {(Object.keys(KIND_LABEL) as ArcadeCheckItem['kind'][]).map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
            </select>
            {it.kind === 'multi' && (
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Switch checked={it.good !== false} onCheckedChange={(v) => update(i, { good: v })} />
                {it.good !== false ? t('people-learning.contenteditortab.debe_marcarse', 'Debe marcarse') : t('people-learning.contenteditortab.intruso', 'Intruso (no marcar)')}
              </label>
            )}
            <div className="ml-auto flex gap-0.5">
              <Button type="button" variant="ghost" size="icon" aria-label={t('common.a11y.move_up')} className="h-7 w-7" onClick={() => move(i, -1)} disabled={i === 0}><ArrowUp className="h-3.5 w-3.5" /></Button>
              <Button type="button" variant="ghost" size="icon" aria-label={t('common.a11y.move_down')} className="h-7 w-7" onClick={() => move(i, 1)} disabled={i === items.length - 1}><ArrowDown className="h-3.5 w-3.5" /></Button>
              <Button type="button" variant="ghost" size="icon" aria-label={t('common.a11y.delete')} className="h-7 w-7 text-destructive" onClick={() => onChange(items.filter((_, j) => j !== i))}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
          <I18nFields label={t('people-learning.contenteditortab.texto_del_item', 'Texto')} value={it.label} onChange={(v) => update(i, { label: v })} />
          <I18nFields label={t('people-learning.contenteditortab.subtexto', 'Subtexto (opcional)')} value={it.sub ?? { es: '' }} onChange={(v) => update(i, { sub: v.es ? v : undefined })} />
          {it.kind === 'select' && (
            <div className="space-y-1.5 pl-2">
              <Label className="text-xs">{t('people-learning.contenteditortab.opciones', 'Opciones (marcá la correcta)')}</Label>
              {(it.options ?? []).map((o, oi) => (
                <div key={o.id} className="flex items-center gap-2">
                  <input
                    type="radio" name={`correct-${it.id}`} checked={!!o.correct}
                    onChange={() => update(i, { options: (it.options ?? []).map((x, xi) => ({ ...x, correct: xi === oi })) })}
                  />
                  <Input
                    value={o.label.es} className="h-7 text-xs"
                    onChange={(e) => update(i, { options: (it.options ?? []).map((x, xi) => (xi === oi ? { ...x, label: { ...x.label, es: e.target.value } } : x)) })}
                  />
                  <Button type="button" variant="ghost" size="icon" aria-label={t('common.a11y.delete')} className="h-7 w-7 text-destructive" onClick={() => update(i, { options: (it.options ?? []).filter((_, xi) => xi !== oi) })}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => update(i, { options: [...(it.options ?? []), { id: `o${(it.options?.length ?? 0) + 1}`, label: { es: '' } }] })}>
                <Plus className="mr-1 h-3 w-3" /> {t('people-learning.contenteditortab.agregar_opcion', 'Agregar opción')}
              </Button>
            </div>
          )}
        </div>
      ))}
      {items.length < max && (
        <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => onChange([...items, { id: `t${items.length + 1}_${Date.now().toString(36).slice(-3)}`, kind: 'toggle', label: { es: '' } }])}>
          <Plus className="mr-1 h-3 w-3" /> {t('people-learning.contenteditortab.agregar_item', 'Agregar ítem')}
        </Button>
      )}
    </div>
  );
}

function StepEditorDialog({ step, onClose, onSave }: {
  step: Step;
  onClose: () => void;
  onSave: (s: Step) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Step>(() => JSON.parse(JSON.stringify(step)));
  const [dataJson, setDataJson] = useState(() => JSON.stringify(step.type === 'minigame' ? (step as any).data : {}, null, 2));
  const [jsonError, setJsonError] = useState('');
  const [advanced, setAdvanced] = useState(false);
  const isVideo = draft.type === 'video';
  const isCheck = draft.type === 'check';
  const isMinigame = draft.type === 'minigame';
  const items: ArcadeCheckItem[] = ((draft as any).items ?? []) as ArcadeCheckItem[];

  const submit = () => {
    const next = JSON.parse(JSON.stringify(draft)) as Step;
    if (isMinigame) {
      try { (next as any).data = JSON.parse(dataJson); }
      catch (e: any) { setJsonError(`JSON inválido: ${e.message}`); return; }
    }
    if (isCheck && items.length === 0) { setJsonError(t('people-learning.contenteditortab.al_menos_un_item', 'Un System Check necesita al menos un ítem')); return; }
    if ((isCheck || isMinigame) && items.some((it) => !it.label.es.trim())) { setJsonError(t('people-learning.contenteditortab.item_sin_texto', 'Hay ítems sin texto en español')); return; }
    if (isMinigame && items.length === 0) delete (next as any).items;
    onSave(next);
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader><DialogTitle>{t('people-learning.contenteditortab.editar_paso')} · {step.id}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <I18nFields label={t('people-learning.contenteditortab.titulo')} value={draft.title} onChange={(v) => setDraft({ ...draft, title: v })} />
          <I18nFields label={t('people-learning.contenteditortab.descripcion')} rows={2} value={draft.desc ?? { es: '' }} onChange={(v) => setDraft({ ...draft, desc: v.es ? v : undefined })} />
          {(isCheck || isMinigame) && (
            <I18nFields label={t('people-learning.contenteditortab.intro', 'Consigna (opcional)')} rows={2} value={(draft as any).intro ?? { es: '' }} onChange={(v) => setDraft({ ...draft, intro: v.es ? v : undefined } as Step)} />
          )}
          {isVideo && (
            <>
              <UrlByLangFields
                label={t('people-learning.contenteditortab.link_del_video_drive_o_youtube')}
                general={(draft as any).videoUrl ?? ''}
                byLang={(draft as any).videoUrlByLang}
                onGeneral={(v) => setDraft({ ...draft, videoUrl: v || undefined } as Step)}
                onByLang={(v) => setDraft({ ...draft, videoUrlByLang: v } as Step)}
                placeholder="https://drive.google.com/file/d/… (compartido con el dominio)"
                disabled={!!(draft as any).byArea}
              />
              {(draft as any).byArea && (
                <p className="text-xs text-muted-foreground">{t('people-learning.contenteditortab.este_paso_usa_el_video_por')}</p>
              )}
              <UrlByLangFields
                label={t('people-learning.contenteditortab.link_externo_se_abre_en_pestana')}
                general={(draft as any).externalLink?.url ?? ''}
                byLang={(draft as any).externalLink?.urlByLang}
                onGeneral={(v) => setDraft({ ...draft, externalLink: v ? { ...((draft as any).externalLink ?? {}), url: v } : undefined } as Step)}
                onByLang={(v) => setDraft({ ...draft, externalLink: { ...((draft as any).externalLink ?? { url: '' }), urlByLang: v } } as Step)}
                placeholder="https://sites.google.com/…"
              />
              {(draft as any).externalLink?.url && (
                <I18nFields
                  label={t('people-learning.contenteditortab.texto_del_link', 'Texto del link (opcional)')}
                  value={(draft as any).externalLink?.label ?? { es: '' }}
                  onChange={(v) => setDraft({ ...draft, externalLink: { ...(draft as any).externalLink, label: v.es ? v : undefined } } as Step)}
                />
              )}
            </>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t('people-learning.contenteditortab.solo_para_el_pais_vacio_todos')}</Label>
              <Input
                value={draft.onlyCountry ?? ''}
                onChange={(e) => setDraft({ ...draft, onlyCountry: e.target.value || undefined })}
                className="h-8 text-sm"
                placeholder="Colombia"
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('people-learning.contenteditortab.opcional_bonus')}</Label>
              <Button
                type="button" variant="outline" size="sm" className="h-8 w-full"
                onClick={() => setDraft({ ...draft, optional: !draft.optional })}
              >
                {draft.optional ? t('people-learning.contenteditortab.si_no_bloquea_el_nivel') : t('people-learning.contenteditortab.no_obligatorio')}
              </Button>
            </div>
          </div>

          {(isCheck || isMinigame) && (
            <div className="space-y-1.5">
              <Label>
                {isCheck
                  ? t('people-learning.contenteditortab.items_del_check', 'Ítems del System Check')
                  : t('people-learning.contenteditortab.switches_del_minijuego', 'Switches que acompañan al minijuego (opcional)')}
              </Label>
              <CheckItemsEditor items={items} onChange={(v) => setDraft({ ...draft, items: v } as Step)} max={isCheck ? 12 : 6} />
            </div>
          )}

          {isMinigame && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>{t('people-learning.contenteditortab.data_del_minijuego', 'Data del minijuego')} · {(draft as any).kind}</Label>
                <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setAdvanced((a) => !a)}>
                  {advanced ? t('people-learning.contenteditortab.ocultar', 'Ocultar') : t('people-learning.contenteditortab.editar_json_avanzado', 'Editar JSON (avanzado)')}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('people-learning.contenteditortab.data_hint', 'La mecánica del minijuego vive en código; acá se ajustan sus datos (opciones, pares, fragmentos, hotspots). Los textos van como {"es": "...", "en": "...", "pt": "..."}.')}
              </p>
              {advanced && (
                <Textarea value={dataJson} onChange={(e) => { setDataJson(e.target.value); setJsonError(''); }} rows={12} className="font-mono text-xs" />
              )}
            </div>
          )}
          {jsonError && <p className="text-xs text-destructive">{jsonError}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit}>Aplicar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
